import React, { useEffect, useMemo, useRef, useState } from 'react';
import KaartMetLegenda from './KaartMetLegenda';
import { fmtId, fmtDatumNL } from '../util/format';
import { lookupPerceel, getKeurkaartCheck, stopAfdichtingsprocedure } from '../api/api';

/* PuttenPaneel — de putten van één inrichting, in één weergave, voor alle drie
   de plekken waar je een inrichting aanmaakt of bijwerkt.

   ── Twee modi, en dat is het hele ontwerp ──────────────────────────────────

   'bewerken'     je stelt de melding zelf samen. Putten toevoegen, velden
                  invullen, putten weghalen. Alles staat open.

   'controleren'  de melding komt uit een PDF. Je voegt niets toe en je typt
                  niets over — die gegevens komen van de melder en horen te
                  blijven staan zoals ze zijn. Het enige wat jij beoordeelt is
                  of de put die het register bij een gemelde coördinaat vond de
                  júiste is. Klopt dat niet, dan kies je een andere put of zeg
                  je dat het er geen van allen is.

   Dat onderscheid zit niet in een vlaggetje hier en daar, maar in de vorm van
   het hele paneel: in 'controleren' verdwijnt de toevoegbalk, verdwijnen de
   grijze register-putten van de kaart, en zijn de cellen platte tekst.

   ── Waarom er bij een bijlage niets te matchen valt ───────────────────────

   Een melding is een eenzijdige rechtshandeling: het gebruiksrecht ontstaat
   door de melding zelf, het waterschap verleent niets. Wij mogen de inhoud van
   die melding dus niet wijzigen. Beslissen dat de melder "eigenlijk" een put
   op 1,2 meter afstand bedoelde, is precies dat: de melder een verklaring
   toeschrijven die hij niet heeft afgelegd.

   Bovendien is die vraag bij een bijlage verzonnen. Wie in het
   meldingsformulier een bestaande put opgeeft, kiest die uit het register — de
   coördinaten zijn dan die van de registerput zelf en het putnummer reist mee.
   Een bijna-treffer kan daar dus alleen ontstaan als de melder juist géén
   registerput koos, oftewel als hij zegt: dit is een put van mij die niet in
   jullie register staat. Dat overrulen is exact wat niet mag.

   Vandaar: `matchen` staat alleen aan bij handmatig opvoeren. Daar transcribeert
   een medewerker een papieren melding en doet híj de identificatie sowieso al.

   Eén uitzondering geldt overal: liggen de coördinaten exact op een registerput,
   dan ís het die put. Dat is geen interpretatie maar een gelijkheid, en dan
   krijgt de put gewoon dat putnummer.

   ── Rustige tabel ─────────────────────────────────────────────────────────

   De vorige versie zette invoervakjes middenin de tabel. Daardoor sprong de
   rijhoogte en las het geheel als een formulier in plaats van als een
   overzicht. Nu zijn de velden onzichtbaar tot je ze aanwijst: dezelfde
   regelhoogte als tekst, een randje pas bij hover of focus. De tabel blijft
   een tabel, en wordt pas een invulveld op het moment dat je er een nodig hebt.

   ── Het puttenmodel, gedeeld door alle drie de schermen ───────────────────

     sleutel              stabiele React-key, verandert nooit
     soort                'gekoppeld' | 'ongeregistreerd' | 'nieuw'
     put_id               gevuld bij 'gekoppeld', anders null
     x_rd, y_rd           getallen (RD)
     perceel              string of null
     max_diepte_mv        maximale boordiepte, m onder maaiveld
     opgegeven_diepte_m  gemeten diepte; bestaat niet bij 'nieuw'
     kandidaten           [{ put_id, afstand_meter, ... }] register-putten in de buurt
     herkomst             'pdf' | 'kaart' | 'zoek' | 'coordinaat' | 'overgenomen'

   `kandidaten` draagt de koppelingscontrole. De backend levert bij een PDF per
   put alle register-putten binnen vijf meter; bij handmatig prikken zoeken we
   ze hier op. Tot nu toe koos de software zelf de dichtstbijzijnde en kon de
   bediener daar niet bij, terwijl de backend het al die tijd al aankon. */

// Voor de legenda.
export const SOORT_LABEL = {
  gekoppeld: 'In het register',
  ongeregistreerd: 'Geboord, niet in register',
  nieuw: 'Te boren',
};

// Voor de Put-kolom.
export const SOORT_NAAM = {
  gekoppeld: 'Gekoppelde put',
  ongeregistreerd: 'Geboorde put',
  nieuw: 'Te boren put',
};

/* De boorstatus van een put in dit paneel, en waar hij vandaan komt.

   Een te boren put is dat per definitie, een geboorde put niet in het
   register ook. Bij een registerput geldt wat de melder zei als hij iets zei
   (`boorstatus`), anders wat het register weet (`registerStatus`). De kolom
   toont het eerste en zet het tweede eronder als het afwijkt: dat is precies
   wat de inlees op de put gaat schrijven. Dezelfde regels als boorstatus.py
   aan de backend, alleen dan met de melder ertussen. */
export const STATUS_TEKST = { geboord: 'Geboord', te_boren: 'Nog te boren', onbekend: 'Onbekend' };
export function boorstatusVan(p) {
  if (p.soort === 'nieuw') return 'te_boren';
  if (p.soort === 'ongeregistreerd') return 'geboord';
  return p.boorstatus || p.registerStatus || 'onbekend';
}

const SOORT_KLEUR = { gekoppeld: '#0071e3', ongeregistreerd: '#0071e3', nieuw: '#ff9500' };

/* Kwetsbare gebieden. Putten hierbinnen mogen juridisch niet via een melding
   geregistreerd worden — dat kan alleen met een vergunning. Deze regel geldt
   dus voor het hele meldingsspoor: zo'n put verschijnt niet op de kaart om aan
   te klikken, niet in het zoekresultaat op putnummer, niet als kandidaat om aan
   te koppelen, en een coördinaat binnen zo'n gebied wordt geweigerd.

   (Bij vergunningen mag het wél; dit paneel dient nu alleen het meldingsspoor.
   Komt er een vergunningsvariant, dan wordt dit een prop in plaats van een
   vaste regel.) */
export const KWETSBARE_TYPES = ['attentie', 'beperkt_beschermd', 'beschermd', 'natura2000'];

// Een put uit het register: de db-trigger houdt `gebieden` per put bij.
export const isMeldbaar = (put) =>
  !(put.gebieden || []).some((g) => KWETSBARE_TYPES.includes(g.type));

/* Voor een los punt bestaat die kolom niet, dus doen we het hier: puntprikking
   tegen de gebiedsgeometrieën die de kaart toch al geladen heeft. Ray casting;
   coördinaten zijn RD, dus gewoon vlak rekenen. */
function puntInRing(x, y, ring) {
  let binnen = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i], [xj, yj] = ring[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) binnen = !binnen;
  }
  return binnen;
}

function puntInGeometrie(x, y, geom) {
  if (!geom) return false;
  const vlakken = geom.type === 'Polygon' ? [geom.coordinates]
    : geom.type === 'MultiPolygon' ? geom.coordinates : [];
  return vlakken.some((vlak) => {
    if (!vlak.length || !puntInRing(x, y, vlak[0])) return false;
    // Gaten in de polygoon tellen niet mee als "binnen".
    return !vlak.slice(1).some((gat) => puntInRing(x, y, gat));
  });
}

/* Alle gebieden waar een punt in ligt. Eén implementatie voor twee vragen:
   mag hier een put staan, en waar ligt hij dan in. */
export const gebiedenOpPunt = (x, y, gebieden) =>
  (gebieden || []).filter((g) => puntInGeometrie(x, y, g.geometrie));

export const kwetsbaarOpPunt = (x, y, gebieden) =>
  gebiedenOpPunt(x, y, gebieden).find((g) => KWETSBARE_TYPES.includes(g.type)) || null;

/* Omhullende rechthoek van een (multi)polygoon. */
function omhullende(geom) {
  const vlakken = geom?.type === 'Polygon' ? [geom.coordinates]
    : geom?.type === 'MultiPolygon' ? geom.coordinates : [];
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  vlakken.forEach((vlak) => (vlak[0] || []).forEach(([x, y]) => {
    if (x < x0) x0 = x;
    if (y < y0) y0 = y;
    if (x > x1) x1 = x;
    if (y > y1) y1 = y;
  }));
  return [x0, y0, x1, y1];
}

/* Toets of een punt in een kwetsbaar gebied ligt, gemaakt om vaak aangeroepen
   te worden: bij elke muisbeweging over de kaart.

   Twee besparingen. De omhullende rechthoek van elk gebied wordt één keer
   berekend en wijst de meeste gebieden meteen af, zodat de puntprikking alleen
   nog draait voor de handvol vlakken waar de muis werkelijk boven zweeft. En
   het laatste antwoord wordt onthouden per hele meter — een muis levert
   tientallen bewegingen per seconde op binnen dezelfde vierkante meter. */
export function maakKwetsbaarToets(gebieden) {
  const vlakken = (gebieden || [])
    .filter((g) => KWETSBARE_TYPES.includes(g.type) && g.geometrie)
    .map((g) => ({ g, bb: omhullende(g.geometrie) }));
  let vorigeX = null, vorigeY = null, vorig = null;
  return (x, y) => {
    const rx = Math.round(x), ry = Math.round(y);
    if (rx === vorigeX && ry === vorigeY) return vorig;
    vorigeX = rx;
    vorigeY = ry;
    const treffer = vlakken.find(({ g, bb }) =>
      rx >= bb[0] && rx <= bb[2] && ry >= bb[1] && ry <= bb[3]
      && puntInGeometrie(rx, ry, g.geometrie));
    vorig = treffer ? treffer.g : null;
    return vorig;
  };
}

let _volgnummer = 0;
/* Wat het register over een put weet, naast wat de melder zegt. */
const uitRegisterStatus = (rp) => ({
  boorstatus: null,
  registerStatus: rp.boorstatus_code || null,
  registerDiepte: rp.opgegeven_diepte_m ?? null,
  gemetenDiepte: rp.gemeten_diepte_m ?? null,
});

export const nieuweSleutel = () => `put-${++_volgnummer}`;

const afstandTussen = (ax, ay, bx, by) => Math.hypot(ax - bx, ay - by);

// Komma als decimaalteken, want zo schrijf je meters in het Nederlands.
const meters = (v) => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  if (!Number.isFinite(n)) return null;
  return String(n).replace('.', ',');
};

/* Register-putten binnen `straal` meter, dichtstbij eerst. Dezelfde straal als
   de backend hanteert bij het inlezen van een PDF, zodat handmatig prikken en
   een PDF dezelfde kandidaten opleveren. */
/* Exacte treffer. De deeplink rondt RD-coördinaten af op hele meters, het
   register bewaart twee decimalen; "exact" is dus: dezelfde hele meter. Ligt
   een put daar, dan is het díe put — geen keuze, geen vraag. */
export const exacteTreffer = (x, y, registerPutten) =>
  registerPutten.find((rp) => rp.x_rd != null && rp.y_rd != null
    && Math.round(+rp.x_rd) === Math.round(x) && Math.round(+rp.y_rd) === Math.round(y)) || null;

export const zoekKandidaten = (x, y, registerPutten, straal = 10) => {
  if (!Number.isFinite(x) || !Number.isFinite(y)) return [];
  return registerPutten
    .filter((rp) => rp.x_rd != null && rp.y_rd != null)
    .map((rp) => ({ ...rp, afstand_meter: Math.round(afstandTussen(x, y, +rp.x_rd, +rp.y_rd) * 100) / 100 }))
    .filter((rp) => rp.afstand_meter <= straal)
    .sort((a, b) => a.afstand_meter - b.afstand_meter);
};

function Stip({ soort, groot = false }) {
  const kleur = SOORT_KLEUR[soort] || '#86868b';
  const hol = soort === 'ongeregistreerd';
  const d = groot ? 11 : 9;
  return (
    <span className="pp-stip" style={{
      width: d, height: d,
      background: hol ? '#fff' : kleur,
      boxShadow: hol ? `inset 0 0 0 2.5px ${kleur}` : 'none',
    }} />
  );
}

/* Cel die pas een invoerveld wordt als je 'm aanwijst. In 'controleren' is het
   simpelweg tekst — geen uitgegrijsd veld, want een uitgegrijsd veld nodigt uit
   tot klikken en levert dan niets op. */
function Cel({ waarde, onChange, bewerkbaar, eenheid, plaatshouder, breed = 84 }) {
  if (!bewerkbaar) {
    /* Ook zonder invoerveld dezelfde opbouw — waarde plus losse eenheid — want
       anders staan de getallen in een kolom met gemengde cellen niet recht
       onder elkaar. */
    if (eenheid) {
      const m = meters(waarde);
      return m == null
        ? <span className="pp-leeg">—</span>
        : <span className="pp-cel"><span>{m}</span><span className="pp-eenheid">{eenheid}</span></span>;
    }
    return waarde ? <span>{waarde}</span> : <span className="pp-leeg">—</span>;
  }
  return (
    <span className="pp-cel">
      <input
        className="pp-cel-invoer"
        style={{ width: breed }}
        value={waarde ?? ''}
        placeholder={plaatshouder}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => onChange(e.target.value)}
      />
      {eenheid && <span className="pp-eenheid">{eenheid}</span>}
    </span>
  );
}

/* Keuzemenu in de Put-kolom.

   Bewust geen <select>: die toont de optietekst ook als hij dicht is, en dan
   staat "P00118 — 1,2 m" in een kolom waar alleen het putnummer hoort. Dicht
   toont dit menu wat de put ís; open toont het waaruit je kiest, mét de
   afstand en de diepte die het onderscheid maken. */
function PutKeuze({ p, onKies }) {
  const [open, setOpen] = useState(false);
  const wrapper = useRef(null);

  useEffect(() => {
    const buiten = (e) => {
      if (wrapper.current && !wrapper.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', buiten);
    return () => document.removeEventListener('mousedown', buiten);
  }, []);

  const label = p.soort === 'gekoppeld'
    ? <span className="mono">{fmtId(p.put_id)}</span>
    : SOORT_NAAM[p.soort];

  const kies = (waarde) => { onKies(p, waarde); setOpen(false); };

  return (
    <span className="pp-keuze" ref={wrapper}>
      <button className={`pp-keuze-knop ${open ? 'is-open' : ''}`}
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}>
        {label}
        <span className="pp-keuze-pijl" aria-hidden="true" />
      </button>
      {open && (
        <div className="pp-keuze-menu" onClick={(e) => e.stopPropagation()}>
          {(p.kandidaten || []).length > 0 && (
            <div className="pp-keuze-groep">In het register, hier vlakbij</div>
          )}
          {(p.kandidaten || []).map((k) => (
            <button key={k.put_id}
              className={`pp-keuze-item ${p.put_id === k.put_id ? 'is-aan' : ''}`}
              onClick={() => kies(k.put_id)}>
              <span className="mono pp-keuze-item-titel">{fmtId(k.put_id)}</span>
              <span className="pp-keuze-item-sub">
                {meters(k.afstand_meter)} m
                {k.opgegeven_diepte_m != null && ` · ${meters(k.opgegeven_diepte_m)} m diep`}
                {k.perceel && ` · ${k.perceel}`}
              </span>
            </button>
          ))}
          <div className="pp-keuze-groep">Niet in het register</div>
          <button className={`pp-keuze-item ${p.soort === 'ongeregistreerd' ? 'is-aan' : ''}`}
            onClick={() => kies('ongeregistreerd')}>
            <span className="pp-keuze-item-titel">Geboorde put</span>
            <span className="pp-keuze-item-sub">ligt er al, staat nog niet in het register</span>
          </button>
          <button className={`pp-keuze-item ${p.soort === 'nieuw' ? 'is-aan' : ''}`}
            onClick={() => kies('nieuw')}>
            <span className="pp-keuze-item-titel">Te boren put</span>
            <span className="pp-keuze-item-sub">moet nog geboord worden</span>
          </button>
        </div>
      )}
    </span>
  );
}

/* Put toevoegen. Eén knop in de kop, met een menu erachter.

   Stond eerst als een eigen balk tussen de kaart en de tabel: een titel, twee
   knoppen en een zin. Drie banden chrome voordat je bij de gegevens was, en de
   belangrijkste manier — klikken op de kaart — stond er als bijzin tussen.
   Nu is het één knop, en vertelt het menu welke drie wegen er zijn. */
export function ToevoegMenu({ registerPutten, gekozenIds, onKoppel, onCoordinaat }) {
  const [open, setOpen] = useState(false);      // menu open?
  const [vorm, setVorm] = useState(null);       // null | 'zoek' | 'coordinaat'
  const [zoek, setZoek] = useState('');
  const [x, setX] = useState('');
  const [y, setY] = useState('');
  const wrapper = useRef(null);

  useEffect(() => {
    const buiten = (e) => {
      if (wrapper.current && !wrapper.current.contains(e.target)) {
        setOpen(false); setVorm(null);
      }
    };
    document.addEventListener('mousedown', buiten);
    return () => document.removeEventListener('mousedown', buiten);
  }, []);

  const treffers = useMemo(() => {
    const q = zoek.trim().toLowerCase();
    if (!q) return [];
    return registerPutten
      .filter((p) => !gekozenIds.has(p.put_id))
      .filter((p) =>
        p.put_id.toLowerCase().includes(q) ||
        (p.perceel || '').toLowerCase().includes(q) ||
        String(Math.round(p.x_rd)).includes(q) ||
        String(Math.round(p.y_rd)).includes(q))
      .slice(0, 6);
  }, [zoek, registerPutten, gekozenIds]);

  /* "109427, 403116" in het X-veld plakken mag: zo staan coördinaten in mails
     en documenten, en dan is overtypen in twee velden onnodig gedoe. */
  const zetX = (v) => {
    if (v.includes(',')) {
      const [a, b] = v.split(',').map((t) => t.trim());
      setX(a || '');
      if (b) setY(b);
      return;
    }
    setX(v);
  };

  const sluit = () => { setOpen(false); setVorm(null); setZoek(''); setX(''); setY(''); };

  const bevestigCoordinaat = () => {
    const nx = parseFloat(x), ny = parseFloat(y);
    if (!Number.isFinite(nx) || !Number.isFinite(ny)) return;
    onCoordinaat(nx, ny);
    sluit();
  };

  return (
    <div className="pp-toevoegen" ref={wrapper}>
      <button className={`pp-toevoegknop ${open ? 'is-open' : ''}`}
        onClick={() => { setOpen(!open); setVorm(null); }}>
        <span className="pp-plus">+</span> Put toevoegen
      </button>

      {open && (
        <div className="pp-menu">
          {vorm === null && (
            <>
              {/* Zoeken dient om een bestaande put te koppelen. Kan dat niet,
                  dan zou deze ingang alleen maar naar een dood eind leiden. */}
              {onKoppel && (
                <button className="pp-menu-item" onClick={() => setVorm('zoek')}>
                  <span className="pp-menu-titel">Zoeken op putnummer</span>
                  <span className="pp-menu-sub">een put die al in het register staat</span>
                </button>
              )}
              <button className="pp-menu-item" onClick={() => setVorm('coordinaat')}>
                <span className="pp-menu-titel">Coördinaat invoeren</span>
                <span className="pp-menu-sub">X en Y in RD</span>
              </button>
              <div className="pp-menu-slot">
                {onKoppel
                  ? <>Of klik op de kaart: een <b>grijze</b> put koppelt hem, een lege plek maakt een nieuwe put.</>
                  : <>Of klik op de kaart om een put te plaatsen. De <b>grijze</b> putten staan al in het register; die kun je alleen bekijken.</>}
              </div>
            </>
          )}

          {vorm === 'zoek' && (
            <div className="pp-menu-vorm">
              <input autoFocus value={zoek} onChange={(e) => setZoek(e.target.value)}
                placeholder="Putnummer, perceel of coördinaat" />
              {zoek.trim() && (treffers.length === 0
                ? <div className="pp-geen-treffers">Geen putten gevonden voor “{zoek}”</div>
                : (
                  <div className="pp-treffers">
                    {treffers.map((p) => (
                      <button key={p.put_id} className="pp-treffer"
                        onClick={() => { onKoppel(p); sluit(); }}>
                        <span className="mono pp-treffer-id">{fmtId(p.put_id)}</span>
                        <span className="pp-treffer-sub">
                          {p.perceel || 'geen perceel'} · {Math.round(p.x_rd)}, {Math.round(p.y_rd)}
                        </span>
                      </button>
                    ))}
                  </div>
                ))}
            </div>
          )}

          {vorm === 'coordinaat' && (
            <div className="pp-menu-vorm">
              <div className="pp-coord-invoer">
                <label>X (RD)
                  <input autoFocus value={x} onChange={(e) => zetX(e.target.value)} inputMode="decimal" />
                </label>
                <label>Y (RD)
                  <input value={y} onChange={(e) => setY(e.target.value)} inputMode="decimal"
                    onKeyDown={(e) => e.key === 'Enter' && bevestigCoordinaat()} />
                </label>
              </div>
              <div className="pp-menu-voet">
                <span className="pp-hint">Je mag ook “109427, 403116” in het X-veld plakken.</span>
                <button className="btn btn-primary btn-sm" onClick={bevestigCoordinaat}>Toevoegen</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* De kolom Boorstatus. Leest wat boorstatusVan zegt; bij een registerput mag
   een behandelaar het in bewerkstand overnemen van de melder (papieren
   melding) met een keuzelijst. Wijkt het antwoord af van wat het register
   had, dan staat dat eronder -- dat is wat er straks wordt geschreven. */
function BoorstatusCel({ p, bewerken, onKies }) {
  const status = boorstatusVan(p);
  const register = p.soort === 'gekoppeld' ? (p.registerStatus || 'onbekend') : null;
  const anders = p.soort === 'gekoppeld' && p.boorstatus && p.boorstatus !== register;
  if (bewerken && p.soort === 'gekoppeld') {
    return (
      <span className="pp-cel pp-status-cel">
        <select className="pp-cel-invoer pp-status-keuze" value={p.boorstatus || ''}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => onKies(e.target.value || null)}>
          <option value="">{STATUS_TEKST[register]} (register)</option>
          <option value="geboord">Geboord</option>
          <option value="te_boren">Nog te boren</option>
        </select>
        {anders && <span className="pp-was">was: {STATUS_TEKST[register].toLowerCase()}</span>}
      </span>
    );
  }
  return (
    <span className="pp-status-cel">
      <span className={status === 'onbekend' ? 'pp-leeg' : ''}>{STATUS_TEKST[status]}</span>
      {anders && <span className="pp-was">was: {STATUS_TEKST[register].toLowerCase()}</span>}
    </span>
  );
}

/* De kolom Diepte: gemeten staat vast, opgegeven is te wijzigen zolang de put
   geboord is, en een te boren put heeft er geen. */
function DiepteCel({ p, bewerken, onChange }) {
  if (boorstatusVan(p) === 'te_boren') return <span className="pp-leeg">—</span>;
  if (p.gemetenDiepte != null) {
    return <span className="pp-cel"><span>{meters(p.gemetenDiepte)}</span><span className="pp-eenheid">m</span></span>;
  }
  const anders = p.soort === 'gekoppeld' && p.registerDiepte != null
    && String(p.opgegeven_diepte_m || '') !== '' && Number(p.opgegeven_diepte_m) !== Number(p.registerDiepte);
  return (
    <span className="pp-status-cel">
      <Cel waarde={p.opgegeven_diepte_m} eenheid="m" bewerkbaar={bewerken} onChange={onChange} />
      {anders && <span className="pp-was">was: {meters(p.registerDiepte)} m</span>}
    </span>
  );
}

export default function PuttenPaneel({
  putten,
  onChange,
  modus = 'bewerken',                 // 'bewerken' | 'controleren'
  registerPutten = [],
  gebieden = [],
  bijlage = null,
  hoogte = 340,
  /* Alleen bij handmatig opvoeren mag een medewerker zeggen dat een opgegeven
     put een bestaande registerput is. Zie de toelichting bovenaan. */
  matchen = modus === 'bewerken',
  matchStraal = 10,
  /* Blokkeren kwetsbare gebieden hier? Bij een melding wel, bij een vergunning
     niet — daar heeft het bevoegd gezag de afweging al gemaakt en wil je alleen
     zien waar de put ligt. */
  kwetsbaarBlokkeert = true,
}) {
  const bewerken = modus === 'bewerken';
  const [actief, setActief] = useState(null);      // aangewezen rij
  /* De openstaande vraag bij het toevoegen van een put. Zolang die er is, is de
     put nog niet aan de lijst toegevoegd: een onbeantwoorde vraag hoort niet
     als halve rij in de tabel te staan. */
  const [vraag, setVraag] = useState(null);
  const [vraagStap, setVraagStap] = useState('kiezen');   // 'kiezen' | 'geboord'
  const [weigering, setWeigering] = useState(null);       // waarom een plek niet mag
  /* De put waarvan de gemelde plek botst met een registerput die eruit gaat.
     Null zolang er niets aan de hand is; anders de rij uit de tabel. */
  const [blokkadeVenster, setBlokkadeVenster] = useState(null);
  const [blokkadeBezig, setBlokkadeBezig] = useState(false);
  const [blokkadeFout, setBlokkadeFout] = useState(null);

  const gekozenIds = useMemo(
    () => new Set(putten.filter((p) => p.put_id).map((p) => p.put_id)), [putten]);

  /* Alleen putten waar een melding überhaupt over mag gaan. Eén keer filteren,
     en overal dezelfde lijst gebruiken: op de kaart, in het zoekresultaat en
     als kandidaat. Anders lekt er via één van die drie alsnog een put uit een
     kwetsbaar gebied naar binnen. */
  const meldbaar = useMemo(
    () => (kwetsbaarBlokkeert ? registerPutten.filter(isMeldbaar) : registerPutten),
    [registerPutten, kwetsbaarBlokkeert]);

  /* De lijst ook in een ref, want de aanvullingen hieronder (perceel, Keurkaart)
     komen asynchroon terug. Zonder ref rekent zo'n late callback met de lijst
     van het moment waarop hij vertrok, en gooit hij de put die er intussen bij
     kwam er weer uit — precies wat er gebeurde bij een put die je toevoegt en
     waarvan het perceel meteen wordt opgezocht. */
  const puttenRef = useRef(putten);
  puttenRef.current = putten;

  const wijzig = (sleutel, velden) =>
    onChange(puttenRef.current.map((p) => (p.sleutel === sleutel ? { ...p, ...velden } : p)));
  const verwijder = (sleutel) => onChange(puttenRef.current.filter((p) => p.sleutel !== sleutel));

  /* Perceel en maximale boordiepte automatisch ophalen zodra een put ergens is
     neergezet. Zat eerder alleen in de modal; nu bij élke manier van toevoegen,
     want het is steeds dezelfde vraag aan dezelfde bron. */
  const vulAan = async (sleutel, x, y) => {
    try {
      const r = await lookupPerceel(x, y);
      if (r?.data?.perceel) wijzig(sleutel, { perceel: r.data.perceel });
    } catch { /* stil: de bediener kan het intikken */ }
    if (!bijlage) return;
    try {
      const r = await getKeurkaartCheck(x, y, bijlage);
      if (r?.data?.max_diepte_m != null) wijzig(sleutel, { max_diepte_mv: String(r.data.max_diepte_m) });
    } catch { /* stil */ }
  };

  const koppelRegisterPut = (rp) => {
    if (gekozenIds.has(rp.put_id)) return;
    const sleutel = nieuweSleutel();
    onChange([...puttenRef.current, {
      sleutel, soort: 'gekoppeld', put_id: rp.put_id, uitRegister: true,
      x_rd: +rp.x_rd, y_rd: +rp.y_rd, perceel: rp.perceel || null,
      max_diepte_mv: rp.max_diepte_mv != null ? String(rp.max_diepte_mv) : '',
      opgegeven_diepte_m: rp.opgegeven_diepte_m != null ? String(rp.opgegeven_diepte_m) : '',
      ...uitRegisterStatus(rp),
      kandidaten: [{ ...rp, afstand_meter: 0 }], herkomst: 'zoek',
    }]);
    setActief(sleutel);
  };

  const putOpCoordinaat = (x, y, herkomst) => {
    const kwetsbaar = kwetsbaarBlokkeert ? kwetsbaarOpPunt(x, y, gebieden) : null;
    if (kwetsbaar) {
      setWeigering(kwetsbaar.naam
        ? `Deze plek ligt in ${kwetsbaar.naam}. Putten in een kwetsbaar gebied kunnen niet via een melding worden geregistreerd.`
        : 'Deze plek ligt in een kwetsbaar gebied. Putten daar kunnen niet via een melding worden geregistreerd.');
      return;
    }
    setWeigering(null);
    const sleutel = nieuweSleutel();
    const exact = exacteTreffer(x, y, meldbaar);
    if (exact) {
      // Zelfde plek = zelfde put. Niets te kiezen.
      onChange([...puttenRef.current, {
        sleutel, soort: 'gekoppeld', put_id: exact.put_id, uitRegister: true,
        x_rd: +exact.x_rd, y_rd: +exact.y_rd, perceel: exact.perceel || null,
        max_diepte_mv: exact.max_diepte_mv != null ? String(exact.max_diepte_mv) : '',
        opgegeven_diepte_m: exact.opgegeven_diepte_m != null ? String(exact.opgegeven_diepte_m) : '',
        ...uitRegisterStatus(exact),
        kandidaten: [{ ...exact, afstand_meter: 0 }], herkomst,
      }]);
      setActief(sleutel);
      return;
    }
    /* Geen exacte treffer: dit wordt een nieuwe put. Liggen er registerputten
       in de buurt, dan bewaren we die als kandidaten — niet om er zelf een uit
       te kiezen, maar zodat de medewerker in de Put-kolom kan zeggen dat het
       een van die bestaande putten is. */
    const kandidaten = matchen
      ? zoekKandidaten(x, y, meldbaar, matchStraal).filter((k) => !gekozenIds.has(k.put_id))
      : [];
    /* Liggen er geregistreerde putten in de buurt, dan is "te boren put" net
       zo goed een bewering als "put P00120". Die vraag stellen we meteen, in
       een modal, in plaats van een halfslachtige rij in de tabel te zetten. */
    if (kandidaten.length) { setVraag({ x, y, kandidaten, herkomst }); return; }
    voegNieuwToe(x, y, 'nieuw', [], herkomst);
  };

  // Een put daadwerkelijk in de lijst zetten, met of zonder registerkoppeling.
  const voegNieuwToe = (x, y, soort, kandidaten, herkomst) => {
    const sleutel = nieuweSleutel();
    onChange([...puttenRef.current, {
      sleutel, soort, put_id: null, x_rd: x, y_rd: y, perceel: null,
      max_diepte_mv: '', opgegeven_diepte_m: '', boorstatus: null, registerStatus: null,
      kandidaten, herkomst,
    }]);
    setActief(sleutel);
    vulAan(sleutel, x, y);
  };

  const koppelUitVraag = (k, herkomst) => {
    const sleutel = nieuweSleutel();
    onChange([...puttenRef.current, {
      sleutel, soort: 'gekoppeld', put_id: k.put_id, uitRegister: true,
      x_rd: +k.x_rd, y_rd: +k.y_rd, perceel: k.perceel || null,
      max_diepte_mv: k.max_diepte_mv != null ? String(k.max_diepte_mv) : '',
      opgegeven_diepte_m: k.opgegeven_diepte_m != null ? String(k.opgegeven_diepte_m) : '',
      ...uitRegisterStatus(k),
      kandidaten: [{ ...k, afstand_meter: 0 }], herkomst,
    }]);
    setActief(sleutel);
  };

  /* De behandelaar zegt namens de melder wat de boorstatus van een registerput
     is. Wordt het te boren, dan hoort er een vers maximum bij voor het doel van
     deze melding; dat halen we op zoals bij een nieuwe put. */
  const kiesStatus = async (p, status) => {
    wijzig(p.sleutel, { boorstatus: status });
    if (status === 'te_boren' && bijlage) {
      try {
        const r = await getKeurkaartCheck(p.x_rd, p.y_rd, bijlage);
        if (r?.data?.max_diepte_m != null) wijzig(p.sleutel, { max_diepte_mv: String(r.data.max_diepte_m) });
      } catch { /* stil: de bediener kan het intikken */ }
    }
  };

  /* Eén handler voor de keuzelijst in de Put-kolom. De waarde is óf een soort,
     óf het putnummer van een registerput in de buurt. */
  const kiesInCel = (p, waarde) => {
    if (waarde === 'nieuw' || waarde === 'ongeregistreerd') {
      wijzig(p.sleutel, { soort: waarde, put_id: null, uitRegister: false });
      return;
    }
    const k = (p.kandidaten || []).find((c) => c.put_id === waarde);
    if (!k) return;
    wijzig(p.sleutel, {
      soort: 'gekoppeld', put_id: k.put_id, uitRegister: true, perceel: k.perceel || p.perceel,
      x_rd: +k.x_rd, y_rd: +k.y_rd,
      opgegeven_diepte_m: k.opgegeven_diepte_m != null
        ? String(k.opgegeven_diepte_m) : p.opgegeven_diepte_m,
      ...uitRegisterStatus(k),
    });
  };

  /* Procedure intrekken vanuit dit scherm. Lukt dat, dan is de put weer een
     gewone registerput — en lag hij op dezelfde hele meter als de gemelde put,
     dan is hij ook meteen de put waar deze melding aan hoort. Dat is precies
     wat er zonder blokkade automatisch was gebeurd, dus dat doen we hier alsnog
     in plaats van de behandelaar de bijlage opnieuw te laten inlezen. */
  const trekProcedureIn = async (p) => {
    setBlokkadeBezig(true);
    setBlokkadeFout(null);
    try {
      await stopAfdichtingsprocedure(p.blokkade.put_id);
      wijzig(p.sleutel, p.blokkade.exact
        ? { soort: 'gekoppeld', put_id: p.blokkade.put_id, uitRegister: true, blokkade: null }
        : { blokkade: null });
      setBlokkadeVenster(null);
    } catch (e) {
      setBlokkadeFout(e.response?.data?.detail || e.message);
    } finally {
      setBlokkadeBezig(false);
    }
  };

  const kaartPunten = useMemo(() => {
    const rest = bewerken
      ? meldbaar.filter((rp) => !gekozenIds.has(rp.put_id)).map((rp) => ({
          put_id: rp.put_id, x_rd: rp.x_rd, y_rd: rp.y_rd, perceel: rp.perceel,
          afgedicht: true, onClick: () => koppelRegisterPut(rp),
        }))
      : [];
    const eigen = putten.map((p) => ({
      put_id: p.put_id || SOORT_NAAM[p.soort],
      x_rd: p.x_rd, y_rd: p.y_rd, perceel: p.perceel,
      soort: p.soort, geselecteerd: p.sleutel === actief,
      onClick: () => setActief(p.sleutel),
    }));
    return [...rest, ...eigen];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [putten, meldbaar, gekozenIds, actief, bewerken]);

  /* Geen puntlegenda meer onder de kaart: de stippen staan in de tabel
     ernaast, met dezelfde kleuren. Twee keer hetzelfde uitleggen maakt de
     kaart alleen langer. */

  return (
    <div className={`pp ${bewerken ? 'pp--bewerken' : 'pp--controleren'}`}>
      <div className="pp-kop">
        <h3 className="pp-titel">Putten</h3>
        <span className="pp-aantal">{putten.length}</span>
        <span className="pp-kop-vul" />
        {bewerken && (
          <ToevoegMenu
            registerPutten={meldbaar}
            gekozenIds={gekozenIds}
            onKoppel={koppelRegisterPut}
            onCoordinaat={(x, y) => putOpCoordinaat(x, y, 'coordinaat')}
          />
        )}
      </div>

      <KaartMetLegenda
        punten={kaartPunten}
        overlays={gebieden}
        hoogte={hoogte}
        fitBijElkeUpdate={false}
        onKaartClick={bewerken ? ((x, y) => putOpCoordinaat(Math.round(x), Math.round(y), 'kaart')) : null}
        focusPunten={actief ? putten.filter((p) => p.sleutel === actief) : null}
      />

      {weigering && (
        <div className="pp-weigering" role="alert">
          {weigering}
          <button className="pp-weigering-weg" onClick={() => setWeigering(null)}
            aria-label="Melding sluiten">×</button>
        </div>
      )}

      {putten.length === 0 ? (
        <div className="pp-leegstand">
          Nog geen putten. Klik op de kaart, of voeg er een toe op putnummer of coördinaat.
        </div>
      ) : (
        <table className="pp-tabel">
          <colgroup>
            <col className="k-put" /><col className="k-coord" /><col className="k-perceel" />
            <col className="k-status" /><col className="k-max" /><col className="k-werkelijk" /><col className="k-acties" />
          </colgroup>
          <thead>
            <tr>
              <th>Put</th>
              <th>Coördinaten (RD)</th>
              <th>Perceel</th>
              <th>Boorstatus</th>
              <th className="pp-rechts">Max. diepte</th>
              <th className="pp-rechts">Diepte</th>
              <th className="pp-acties" />
            </tr>
          </thead>
          <tbody>
            {putten.map((p) => {
              return (
                <React.Fragment key={p.sleutel}>
                  <tr className={p.sleutel === actief ? 'is-actief' : ''}
                      onClick={() => setActief(p.sleutel)}>
                    <td>
                      <span className="pp-put">
                        <Stip soort={p.soort} groot />
                        <span className="pp-put-tekst">
                          {bewerken && !p.uitRegister ? (
                            /* Een put die uit het register komt is wat hij is.
                               Je kunt van P00178 niet verklaren dat hij nog
                               geboord moet worden of niet geregistreerd is —
                               hij staat er. Vergissing? Verwijderen en opnieuw
                               kiezen. Alleen bij een zelf geprikte plek is de
                               soort een echte vraag. */
                            <PutKeuze p={p} onKies={kiesInCel} />
                          ) : (
                            <span className="pp-put-naam">
                              {p.put_id
                                ? <span className="mono">{fmtId(p.put_id)}</span>
                                : SOORT_NAAM[p.soort]}
                            </span>
                          )}
                        </span>
                      </span>
                    </td>
                    <td className="mono pp-coord-cel">{Math.round(p.x_rd)}, {Math.round(p.y_rd)}</td>
                    <td>
                      <Cel waarde={p.perceel} bewerkbaar={bewerken && p.soort !== 'gekoppeld'}
                        breed={132} plaatshouder="—"
                        onChange={(v) => wijzig(p.sleutel, { perceel: v })} />
                    </td>
                    <td>
                      <BoorstatusCel p={p} bewerken={bewerken}
                        onKies={(v) => kiesStatus(p, v)} />
                    </td>
                    <td className="pp-rechts">
                      {/* Het maximum hoort bij een put die nog geboord moet
                          worden; bij een geboorde put staat het er niet. Een
                          registerput die de melder als te boren aanwijst krijgt
                          het verse maximum, en dat gaat bij het klaarzetten
                          meteen de put in. */}
                      {boorstatusVan(p) === 'te_boren'
                        ? <Cel waarde={p.max_diepte_mv} eenheid="m"
                            bewerkbaar={bewerken}
                            plaatshouder="verplicht"
                            onChange={(v) => wijzig(p.sleutel, { max_diepte_mv: v })} />
                        : <span className="pp-leeg">—</span>}
                      {p.soort === 'gekoppeld' && boorstatusVan(p) === 'te_boren'
                        && p.kandidaten?.[0]?.max_diepte_mv != null
                        && String(p.kandidaten[0].max_diepte_mv) !== String(p.max_diepte_mv)
                        && <span className="pp-was">was: {meters(p.kandidaten[0].max_diepte_mv)} m</span>}
                    </td>
                    <td className="pp-rechts">
                      <DiepteCel p={p} bewerken={bewerken}
                        onChange={(v) => wijzig(p.sleutel, { opgegeven_diepte_m: v })} />
                    </td>
                    <td className="pp-acties">
                      {bewerken && (
                        <button className="pp-actie pp-actie-weg" title="Put verwijderen"
                          onClick={(e) => { e.stopPropagation(); verwijder(p.sleutel); }}>Verwijderen</button>
                      )}
                    </td>
                  </tr>

                  {/* De melder zegt: hier ligt al een put. Het register zegt:
                      die put gaat eruit. Dat verzwijgen levert stilletjes twee
                      registerputten voor één gat op, dus staat het er gewoon. */}
                  {p.blokkade && (
                    <tr>
                      <td colSpan={6} style={{ paddingTop: 0 }}>
                        <div style={{
                          display: 'flex', alignItems: 'baseline', gap: 10,
                          flexWrap: 'wrap', padding: '9px 12px', borderRadius: 8,
                          background: '#fff7ed', color: '#b25000', fontSize: 13,
                          lineHeight: 1.5,
                        }}>
                          <span style={{ flex: '1 1 320px' }}>
                            Op deze plek staat registerput{' '}
                            <span className="mono">{fmtId(p.blokkade.put_id)}</span>
                            {p.blokkade.toestand === 'afgedicht'
                              ? <>, afgedicht op {fmtDatumNL(p.blokkade.buiten_gebruik_datum)}.</>
                              : <>, waarvan de afdichtingsprocedure loopt sinds {fmtDatumNL(p.blokkade.afdichting_gestart_datum)}.</>}
                            {' '}De melder geeft hier een al geboorde put op.
                          </span>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={(e) => { e.stopPropagation(); setBlokkadeFout(null); setBlokkadeVenster(p); }}
                          >
                            Wat nu?
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}

      {vraag && (
        <div className="pp-overlay" onClick={() => { setVraag(null); setVraagStap('kiezen'); }}>
          <div className="pp-modal" onClick={(e) => e.stopPropagation()}>
            {vraagStap === 'kiezen' ? (
              <>
                <h3 className="pp-modal-titel">Geregistreerde putten dichtbij</h3>
                <div className="pp-modal-lijst">
                  {vraag.kandidaten.map((k) => (
                    <button key={k.put_id} className="pp-modal-keuze"
                      onClick={() => { koppelUitVraag(k, vraag.herkomst); setVraag(null); }}>
                      <span className="mono pp-modal-keuze-titel">{fmtId(k.put_id)}</span>
                      <span className="pp-modal-keuze-sub">
                        {meters(k.afstand_meter)} m
                        {k.opgegeven_diepte_m != null && ` · ${meters(k.opgegeven_diepte_m)} m diep`}
                        {k.perceel && ` · ${k.perceel}`}
                      </span>
                    </button>
                  ))}
                  <button className="pp-modal-keuze pp-modal-keuze--anders"
                    onClick={() => setVraagStap('geboord')}>
                    <span className="pp-modal-keuze-titel">Andere put</span>
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 className="pp-modal-titel">Moet de put nog worden geboord?</h3>
                <div className="pp-modal-knoppen">
                  <button className="btn btn-primary"
                    onClick={() => { voegNieuwToe(vraag.x, vraag.y, 'nieuw', vraag.kandidaten, vraag.herkomst);
                                     setVraag(null); setVraagStap('kiezen'); }}>Ja</button>
                  <button className="btn btn-secondary"
                    onClick={() => { voegNieuwToe(vraag.x, vraag.y, 'ongeregistreerd', vraag.kandidaten, vraag.herkomst);
                                     setVraag(null); setVraagStap('kiezen'); }}>Nee, is al gerealiseerd</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {blokkadeVenster && (() => {
        const b = blokkadeVenster.blokkade;
        if (!b) return null;
        const afgedicht = b.toestand === 'afgedicht';
        return (
          <div className="pp-overlay" onClick={() => setBlokkadeVenster(null)}>
            <div className="pp-modal" onClick={(e) => e.stopPropagation()}>
              <h3 className="pp-modal-titel">Registerput op deze plek</h3>
              <p style={{ marginTop: 0, marginBottom: 12 }}>
                Op {b.afstand_meter === 0 ? 'exact deze plek' : `${b.afstand_meter} meter hiervandaan`} staat
                registerput <span className="mono">{fmtId(b.put_id)}</span>.{' '}
                {afgedicht
                  ? <>Die is afgedicht op {fmtDatumNL(b.buiten_gebruik_datum)}.</>
                  : <>Daarvan loopt de afdichtingsprocedure sinds {fmtDatumNL(b.afdichting_gestart_datum)}.</>}
                {' '}De melder geeft hier een put op die er al ligt. Twee bronnen
                spreken elkaar dus tegen.
              </p>
              <p style={{ marginTop: 0, marginBottom: 12 }}>
                Waarom die put eruit gaat en van wie dat bericht kwam, staat in de
                notities bij{' '}
                <a href={`/beregening/putten/${b.put_id}`} target="_blank" rel="noreferrer">
                  {fmtId(b.put_id)}
                </a>. Namen staan niet in het register; die vindt u via de mailbox
                die daar genoemd is.
              </p>
              <p style={{ marginTop: 0, marginBottom: 16 }}>
                {afgedicht
                  ? <>Een afgedichte put is niet terug te draaien met een knop. Klopt
                      de afdichting niet, dan hoort die eerst rechtgezet te worden op
                      de putpagina. Klopt hij wél, dan gaat deze melding over een put
                      die niet meer bestaat en is een gecorrigeerde melding van de
                      initiatiefnemer nodig.</>
                  : <>Blijkt de put gewoon in gebruik, dan trekt u de procedure hier
                      in en hangt deze melding er alsnog aan. Klopt de procedure wél,
                      dan gaat deze melding over een put die eruit gaat en is een
                      gecorrigeerde melding van de initiatiefnemer nodig.</>}
              </p>
              {blokkadeFout && (
                <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 12px' }}>{blokkadeFout}</p>
              )}
              <div className="pp-modal-knoppen">
                {!afgedicht && (
                  <button
                    className="btn btn-primary"
                    disabled={blokkadeBezig}
                    onClick={() => trekProcedureIn(blokkadeVenster)}
                  >
                    {blokkadeBezig ? 'Bezig…' : 'Afdichtingsprocedure intrekken'}
                  </button>
                )}
                <button className="btn btn-secondary" onClick={() => setBlokkadeVenster(null)}>
                  Sluiten
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
